import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { MouseEventHandler } from "react";
import { useSelector } from "react-redux";

import { Case, Specimen } from "pwapi";

import { AuthState } from "shared/reducers/authReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import selectSortedCaseDetail from "shared/selectors/selectSortedCaseDetail";
import { pieceCountEnumToLabel } from "shared/utils/blockUtils";
import { specimen_source } from "shared/utils/specimenUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { useAppSelector } from "store/hooks";

import ClinicalHistorySection from "./ClinicalHistorySection";
import { SpecimenDialogProps } from "./SpecimenDialogController";
import { isDiagnosable } from "./utils";

export const shouldShowSpecimenDialog = (specimen: Specimen) => {
  return !!(
    specimen &&
    (specimen?.clinical_history ||
      specimen_source(specimen) ||
      specimen?.gross_description)
  );
};

const SpecimenDialog = ({
  specimen,
  caseDetail,
  onEdit,
}: SpecimenDialogProps & { onEdit?: MouseEventHandler<HTMLButtonElement> }) => {
  const source = specimen && specimen_source(specimen);
  const currentUser = useSelector(
    ({ auth }: { auth: AuthState }) => auth?.user
  );
  const sortedCase = useAppSelector(selectSortedCaseDetail);

  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);

  const specimenBlocks = sortedCase.blocks.filter(
    (block) => block.specimen_id === specimen.id
  );
  const noPieceCount = specimenBlocks.every((block) => !block.piece_count);
  const createdBeforePieceCount =
    specimenBlocks.length > 0 &&
    specimenBlocks.every((block) => {
      if (!block.created_at) return false;

      const createdAtDate = new Date(block.created_at);
      const comparisonDate = new Date("2024-07-11");

      return createdAtDate < comparisonDate;
    });
  const digitalPC = caseDetail?.accessioning_lab?.settings?.digital_pc?.active;
  const isDigitalPCWithNoPieceCount = digitalPC && noPieceCount;
  const isCreatedBeforePieceCountWithNoPieceCount =
    createdBeforePieceCount && noPieceCount;

  return (
    <>
      <dl>
        <ClinicalHistorySection clinicalHistory={specimen?.clinical_history} />

        {source && (
          <>
            <dt>Source</dt>
            <dd className="Specimen__content--wordWrap">{source}</dd>
          </>
        )}

        {(specimen?.gross_description ||
          (!specimen?.gross_description && !specimen?.materials_received)) && (
          <>
            <dt>Gross Description</dt>
            {specimen?.gross_description ? (
              <dd className="Specimen__content--wordWrap">
                {specimen?.gross_description}
              </dd>
            ) : (
              !specimen.materials_received && (
                <dd className="Specimen__contentContainer__missing">
                  <FontAwesomeIcon
                    icon="exclamation-circle"
                    color={"var(--red)"}
                  />{" "}
                  Missing Gross Description
                </dd>
              )
            )}
          </>
        )}

        {specimen?.materials_received && (
          <>
            <dt>Materials Received</dt>
            <dd className="Specimen__content--wordWrap">
              {specimen.materials_received}
            </dd>
          </>
        )}

        {!isDigitalPCWithNoPieceCount &&
          !isCreatedBeforePieceCountWithNoPieceCount && (
            <>
              <dt>Piece Map</dt>
              {specimenBlocks && specimenBlocks.length > 0 ? (
                <dd className="Specimen__pieceMap">
                  {specimenBlocks.map((block, i) => (
                    <span
                      key={block.id}
                      className={classNames({
                        Specimen__contentContainer__missing: !block.piece_count,
                      })}
                    >
                      {specimen.name}
                      {block.name}&mdash;
                      {pieceCountEnumToLabel(block.piece_count) || "?"}
                      {i < specimenBlocks.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </dd>
              ) : (
                <dd className="Specimen__contentContainer__missing">
                  <FontAwesomeIcon
                    icon="exclamation-circle"
                    color={"var(--red)"}
                  />{" "}
                  No Blocks Available
                </dd>
              )}
            </>
          )}
      </dl>
      {caseDetail?.source === Case.source.PW &&
        isDiagnosable(caseDetail) &&
        features?.viewerSpecimenEdit &&
        currentUser &&
        specimen.gross_description &&
        caseDetail?.active_assignment?.id === currentUser?.id && (
          <div className="Sidebar__controlContainer">
            <PathologyWatchButton
              onClick={onEdit}
              testId="specimen_edit_pencil"
              className="Sidebar__controlContainer--edit"
            >
              <FontAwesomeIcon icon="pencil-alt" />
            </PathologyWatchButton>
          </div>
        )}
    </>
  );
};

export default SpecimenDialog;
